import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Bot,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Plus,
  RotateCcw,
  ShieldAlert,
  Trash2,
  Waypoints,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/components/ui/use-toast';
import { CredentialDialog } from '@/components/workspaces/CredentialDialog';
import { WorkspaceTokenDialog } from '@/components/workspaces/WorkspaceTokenDialog';
import {
  useAIConfig,
  useCreateCredential,
  useCreateWorkspaceToken,
  useDeleteCredential,
  useDeleteWorkspaceToken,
  useRotateCredential,
  useRotateWorkspaceToken,
  useUpdateAIConfig,
  useWorkspaceCredentials,
  useWorkspaceRole,
  useWorkspaceTokens,
  type CredentialKind,
  type CredentialSecret,
  type IssuedWorkspaceToken,
  type WorkspaceCredential,
  type WorkspaceToken,
  type WorkspaceTokenScope,
} from '@/lib/api';
import { SCOPE_HELP } from '@/lib/workspaceTokens';

const selectClass =
  'flex h-9 w-full rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function WorkspaceSettingsTab() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const { canManage, isLoading: roleLoading } = useWorkspaceRole(wsId);

  if (roleLoading) return <Spinner label="Loading…" />;
  if (!canManage) {
    return (
      <EmptyState
        icon={ShieldAlert}
        title="Owner or admin access required"
        description="Workspace credentials, API tokens and AI configuration are only visible to owners and admins. Ask a workspace owner if you need access."
      />
    );
  }

  return (
    <div className="space-y-6">
      <CredentialsCard wsId={wsId} />
      <ApiTokensCard wsId={wsId} />
      <AIConfigCard wsId={wsId} />
    </div>
  );
}

const KIND_LABEL: Record<WorkspaceCredential['kind'], string> = {
  s3: 'S3',
  ai_provider: 'AI provider',
};

function CredentialsCard({ wsId }: { wsId: string }) {
  const creds = useWorkspaceCredentials(wsId);
  const create = useCreateCredential();
  const rotate = useRotateCredential();
  const remove = useDeleteCredential();
  const toast = useToast();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [rotateTarget, setRotateTarget] = useState<WorkspaceCredential | null>(null);

  function openAdd() {
    setRotateTarget(null);
    setDialogOpen(true);
  }

  function openRotate(cred: WorkspaceCredential) {
    setRotateTarget(cred);
    setDialogOpen(true);
  }

  async function onCreate(input: {
    kind: CredentialKind;
    name: string;
    provider?: string;
    secret: CredentialSecret;
  }) {
    try {
      await create.mutateAsync({ workspaceID: wsId, ...input });
      toast.show('Credential added', 'success');
      setDialogOpen(false);
    } catch (err) {
      toast.show((err as Error).message, 'error');
    }
  }

  async function onRotate(secret: CredentialSecret) {
    if (!rotateTarget) return;
    try {
      await rotate.mutateAsync({ workspaceID: wsId, credentialID: rotateTarget.id, secret });
      toast.show('Secret rotated', 'success');
      setDialogOpen(false);
    } catch (err) {
      toast.show((err as Error).message, 'error');
    }
  }

  async function onDelete(cred: WorkspaceCredential) {
    if (
      !window.confirm(
        `Delete credential “${cred.name}”? Anything referencing it (publish targets, AI config) will stop working.`,
      )
    )
      return;
    try {
      await remove.mutateAsync({ workspaceID: wsId, credentialID: cred.id });
      toast.show('Credential deleted', 'success');
    } catch (err) {
      toast.show((err as Error).message, 'error');
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="inline-flex items-center gap-2">
            <KeyRound className="size-4 text-primary" aria-hidden />
            Credentials
          </CardTitle>
          <CardDescription>
            S3 access keys and AI-provider API keys. Stored encrypted, write-once — only the masked
            fingerprint is ever shown back.
          </CardDescription>
        </div>
        <Button size="sm" onClick={openAdd}>
          <Plus aria-hidden />
          Add
        </Button>
      </CardHeader>
      <CardContent>
        {creds.isLoading ? (
          <Spinner label="Loading credentials…" />
        ) : creds.isError ? (
          <p
            role="alert"
            className="rounded-md border border-negative/40 bg-negative/10 p-3 text-sm text-negative"
          >
            Failed to load credentials: {creds.error.message}
          </p>
        ) : (creds.data?.length ?? 0) === 0 ? (
          <EmptyState
            icon={KeyRound}
            title="No credentials yet"
            description="Add S3 keys to publish translations to a bucket, or an AI-provider key to use your own model quota."
            action={
              <Button size="sm" variant="outline" onClick={openAdd}>
                <Plus aria-hidden />
                Add credential
              </Button>
            }
          />
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border">
            {creds.data!.map((cred) => (
              <li key={cred.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-foreground">
                      {cred.name}
                    </span>
                    <Badge tone="outline">{KIND_LABEL[cred.kind]}</Badge>
                    {cred.provider && <Badge tone="muted">{cred.provider}</Badge>}
                  </div>
                  <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                    {cred.hint ?? '—'}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openRotate(cred)}
                    disabled={rotate.isPending}
                  >
                    <RotateCcw aria-hidden />
                    Rotate
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={`Delete ${cred.name}`}
                    onClick={() => void onDelete(cred)}
                    disabled={remove.isPending}
                  >
                    <Trash2 aria-hidden className="text-negative" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <CredentialDialog
        open={dialogOpen}
        pending={create.isPending || rotate.isPending}
        rotateTarget={rotateTarget}
        onClose={() => setDialogOpen(false)}
        onCreate={onCreate}
        onRotate={onRotate}
      />
    </Card>
  );
}

const SCOPE_TONE: Record<WorkspaceTokenScope, 'muted' | 'primary' | 'warning'> = {
  read: 'muted',
  write: 'primary',
  admin: 'warning',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function tokenMeta(token: WorkspaceToken, now: number): string {
  const parts = [token.tokenPrefix + '…'];
  if (token.expiresAt) {
    parts.push(
      Date.parse(token.expiresAt) <= now
        ? `expired ${formatDate(token.expiresAt)}`
        : `expires ${formatDate(token.expiresAt)}`,
    );
  } else {
    parts.push('never expires');
  }
  parts.push(token.lastUsedAt ? `last used ${formatDate(token.lastUsedAt)}` : 'never used');
  return parts.join(' · ');
}

function ApiTokensCard({ wsId }: { wsId: string }) {
  const tokens = useWorkspaceTokens(wsId);
  const create = useCreateWorkspaceToken();
  const rotate = useRotateWorkspaceToken();
  const remove = useDeleteWorkspaceToken();
  const toast = useToast();

  const [dialogOpen, setDialogOpen] = useState(false);
  // The raw token exists only in this state, from create/rotate until the
  // user dismisses it or leaves the page.
  const [revealed, setRevealed] = useState<IssuedWorkspaceToken | null>(null);
  const [tokenVisible, setTokenVisible] = useState(false);
  const now = Date.now();

  function reveal(issued: IssuedWorkspaceToken) {
    setRevealed(issued);
    setTokenVisible(true);
  }

  async function onCreate(input: { name: string; scope: WorkspaceTokenScope; expiresAt?: string }) {
    try {
      reveal(await create.mutateAsync({ workspaceID: wsId, ...input }));
      setDialogOpen(false);
      toast.show('Token created — copy it now', 'warning');
    } catch (err) {
      toast.show((err as Error).message, 'error');
    }
  }

  async function onRotate(token: WorkspaceToken) {
    if (
      !window.confirm(
        `Rotate “${token.name}”? The current token stops working immediately and the new one is shown once.`,
      )
    )
      return;
    try {
      reveal(await rotate.mutateAsync({ workspaceID: wsId, tokenID: token.id }));
      toast.show('Token rotated — copy it now', 'warning');
    } catch (err) {
      toast.show((err as Error).message, 'error');
    }
  }

  async function onDelete(token: WorkspaceToken) {
    if (
      !window.confirm(
        `Revoke “${token.name}”? Applications using it lose access immediately. This cannot be undone.`,
      )
    )
      return;
    try {
      await remove.mutateAsync({ workspaceID: wsId, tokenID: token.id });
      if (revealed?.id === token.id) setRevealed(null);
      toast.show('Token revoked', 'success');
    } catch (err) {
      toast.show((err as Error).message, 'error');
    }
  }

  async function copyToken() {
    if (!revealed?.token) return;
    try {
      await navigator.clipboard.writeText(revealed.token);
      toast.show('Token copied to clipboard', 'success');
    } catch {
      toast.show('Copy failed — select the text manually', 'error');
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="inline-flex items-center gap-2">
            <Waypoints className="size-4 text-primary" aria-hidden />
            API tokens
          </CardTitle>
          <CardDescription>
            <code>fxw_…</code> tokens let applications work across every project in this workspace.
            Each is scoped read, write or admin, and shown only once.
          </CardDescription>
        </div>
        <Button size="sm" onClick={() => setDialogOpen(true)}>
          <Plus aria-hidden />
          Create
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {revealed && (
          <div className="space-y-2 rounded-md border border-warning/40 bg-warning/10 p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-warning">
                Token for “{revealed.name}”. It won't be shown again, so copy it now.
              </p>
              <Button size="sm" variant="ghost" onClick={() => setRevealed(null)}>
                Done
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                aria-label="New API token"
                value={revealed.token}
                type={tokenVisible ? 'text' : 'password'}
                className="font-mono"
                onFocus={(e) => e.target.select()}
              />
              <Button
                size="icon"
                variant="outline"
                aria-label={tokenVisible ? 'Hide token' : 'Reveal token'}
                onClick={() => setTokenVisible((v) => !v)}
              >
                {tokenVisible ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
              </Button>
              <Button
                size="icon"
                variant="outline"
                onClick={() => void copyToken()}
                aria-label="Copy token"
              >
                <Copy aria-hidden />
              </Button>
            </div>
          </div>
        )}

        {tokens.isLoading ? (
          <Spinner label="Loading API tokens…" />
        ) : tokens.isError ? (
          <p
            role="alert"
            className="rounded-md border border-negative/40 bg-negative/10 p-3 text-sm text-negative"
          >
            Failed to load API tokens: {tokens.error.message}
          </p>
        ) : (tokens.data?.length ?? 0) === 0 ? (
          <EmptyState
            icon={Waypoints}
            title="No API tokens yet"
            description="Create a token for a service, script or integration that needs to read or write this workspace."
            action={
              <Button size="sm" variant="outline" onClick={() => setDialogOpen(true)}>
                <Plus aria-hidden />
                Create token
              </Button>
            }
          />
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border">
            {tokens.data!.map((token) => {
              const expired = token.expiresAt != null && Date.parse(token.expiresAt) <= now;
              return (
                <li key={token.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-foreground">
                        {token.name}
                      </span>
                      <Badge tone={SCOPE_TONE[token.scope]} title={SCOPE_HELP[token.scope]}>
                        {token.scope}
                      </Badge>
                      {expired && <Badge tone="negative">expired</Badge>}
                    </div>
                    <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                      {tokenMeta(token, now)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void onRotate(token)}
                      disabled={rotate.isPending}
                    >
                      <RotateCcw aria-hidden />
                      Rotate
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={`Revoke ${token.name}`}
                      onClick={() => void onDelete(token)}
                      disabled={remove.isPending}
                    >
                      <Trash2 aria-hidden className="text-negative" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      <WorkspaceTokenDialog
        open={dialogOpen}
        pending={create.isPending}
        onClose={() => setDialogOpen(false)}
        onCreate={onCreate}
      />
    </Card>
  );
}

function AIConfigCard({ wsId }: { wsId: string }) {
  const cfg = useAIConfig(wsId);
  const creds = useWorkspaceCredentials(wsId);
  const update = useUpdateAIConfig();
  const toast = useToast();

  const [provider, setProvider] = useState<'' | 'anthropic' | 'openai'>('');
  const [model, setModel] = useState('');
  const [credentialId, setCredentialId] = useState('');
  const [fallbackToCluster, setFallback] = useState(true);

  useEffect(() => {
    if (cfg.data) {
      setProvider(cfg.data.provider ?? '');
      setModel(cfg.data.model ?? '');
      setCredentialId(cfg.data.credentialId ?? '');
      setFallback(cfg.data.fallbackToCluster ?? true);
    }
  }, [cfg.data]);

  const aiCredentials = useMemo(
    () => (creds.data ?? []).filter((c) => c.kind === 'ai_provider'),
    [creds.data],
  );

  const dirty =
    cfg.data != null &&
    (provider !== (cfg.data.provider ?? '') ||
      model !== (cfg.data.model ?? '') ||
      credentialId !== (cfg.data.credentialId ?? '') ||
      fallbackToCluster !== (cfg.data.fallbackToCluster ?? true));

  async function onSave() {
    try {
      await update.mutateAsync({
        workspaceID: wsId,
        provider: provider || undefined,
        model: model.trim() || undefined,
        credentialId: credentialId || undefined,
        fallbackToCluster,
      });
      toast.show('AI config saved', 'success');
    } catch (err) {
      toast.show((err as Error).message, 'error');
    }
  }

  function reset() {
    if (!cfg.data) return;
    setProvider(cfg.data.provider ?? '');
    setModel(cfg.data.model ?? '');
    setCredentialId(cfg.data.credentialId ?? '');
    setFallback(cfg.data.fallbackToCluster ?? true);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2">
          <Bot className="size-4 text-primary" aria-hidden />
          AI model
        </CardTitle>
        <CardDescription>
          Provider and model the translator uses for this workspace. Leave a field empty to inherit
          the cluster default.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {cfg.isLoading ? (
          <Spinner label="Loading AI config…" />
        ) : cfg.isError ? (
          <p
            role="alert"
            className="rounded-md border border-negative/40 bg-negative/10 p-3 text-sm text-negative"
          >
            Failed to load AI config: {cfg.error.message}
          </p>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="ai-provider">Provider</Label>
                <select
                  id="ai-provider"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value as '' | 'anthropic' | 'openai')}
                  className={selectClass}
                >
                  <option value="">Cluster default</option>
                  <option value="anthropic">anthropic</option>
                  <option value="openai">openai</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ai-model">Model</Label>
                <Input
                  id="ai-model"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="provider default"
                  aria-describedby="ai-model-hint"
                />
                <p id="ai-model-hint" className="text-[11px] text-muted-foreground">
                  e.g. <code>claude-opus-4-8</code> or <code>gpt-4o</code>. Empty = provider default.
                </p>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ai-credential">API key credential</Label>
              <select
                id="ai-credential"
                value={credentialId}
                onChange={(e) => setCredentialId(e.target.value)}
                className={selectClass}
              >
                <option value="">Use cluster key</option>
                {aiCredentials.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} {c.provider ? `(${c.provider})` : ''} — {c.hint}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-muted-foreground">
                {aiCredentials.length === 0
                  ? 'No AI-provider credentials yet — add one above to use your own quota.'
                  : 'Pick a workspace AI-provider credential, or fall back to the cluster key.'}
              </p>
            </div>

            <label className="flex items-start gap-2.5 rounded-md border border-border p-3">
              <input
                type="checkbox"
                checked={fallbackToCluster}
                onChange={(e) => setFallback(e.target.checked)}
                className="mt-0.5 size-4 rounded border-input text-primary focus-visible:ring-2 focus-visible:ring-ring"
              />
              <span className="text-sm">
                <span className="font-medium text-foreground">Fall back to the cluster key</span>
                <span className="mt-0.5 block text-[11px] text-muted-foreground">
                  When no workspace credential is set (or it fails), use the cluster-wide provider
                  key instead of erroring.
                </span>
              </span>
            </label>

            <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
              <Button variant="outline" onClick={reset} disabled={!dirty || update.isPending}>
                Reset
              </Button>
              <Button onClick={() => void onSave()} disabled={!dirty || update.isPending}>
                {update.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
